import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { applicationService } from '../../services/firebaseService';

interface VolunteerApplication {
  id?: string;
  name: string;
  email: string;
  phone?: string;
  message?: string;
  submittedAt?: any;
  status?: 'pending' | 'reviewed' | 'accepted' | 'rejected';
}

const VolunteerApplicationsManager: React.FC = () => {
  const { logout } = useAuth();
  const [applications, setApplications] = useState<VolunteerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'reviewed' | 'accepted' | 'rejected'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const data = await applicationService.getApplications('volunteer');
      setApplications(data as VolunteerApplication[]);
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id: string, newStatus: 'pending' | 'reviewed' | 'accepted' | 'rejected') => {
    try {
      await applicationService.updateApplicationStatus('volunteer', id, newStatus);
      await loadApplications();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'Unknown';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'reviewed': return '#17a2b8';
      case 'accepted': return '#28a745';
      case 'rejected': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesFilter = filter === 'all' || app.status === filter;
    const matchesSearch = searchTerm === '' || 
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.phone && app.phone.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="admin-layout">
      <div className="admin-sidebar">
        <h3>Admin Panel</h3>
        <nav>
          <ul>
            <li><Link to="/admin">Dashboard</Link></li>
            <li><Link to="/admin/content">Content</Link></li>
            <li><Link to="/admin/board">Board Members</Link></li>
            <li><Link to="/admin/programs">Programs</Link></li>
            <li><Link to="/admin/events">Events</Link></li>
            <li><Link to="/admin/volunteers">Volunteer Applications</Link></li>
            <li><Link to="/admin/contact">Contact Submissions</Link></li>
            <li><button onClick={logout}>Logout</button></li>
          </ul>
        </nav>
      </div>
      <div className="admin-content">
        <h1>Volunteer Applications</h1>
        <p>View and manage volunteer applications submitted through the website.</p>

        <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                fontSize: '0.9em',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {(['all', 'pending', 'reviewed', 'accepted', 'rejected'] as const).map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={filter === status ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ textTransform: 'capitalize' }}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : filteredApplications.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
            <p>No applications found{filter !== 'all' ? ` with status "${filter}"` : ''}.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {filteredApplications.map(app => (
              <div key={app.id} className="card" style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 0.5rem 0' }}>{app.name}</h3>
                    <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '0.25rem' }}>
                      <strong>Email:</strong> <a href={`mailto:${app.email}`}>{app.email}</a>
                    </div>
                    {app.phone && (
                      <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '0.25rem' }}>
                        <strong>Phone:</strong> <a href={`tel:${app.phone}`}>{app.phone}</a>
                      </div>
                    )}
                    <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '0.5rem' }}>
                      <strong>Submitted:</strong> {formatDate(app.submittedAt)}
                    </div>
                    {app.message && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <strong>Message:</strong>
                        <p style={{ margin: '0.5rem 0 0 0', whiteSpace: 'pre-wrap' }}>{app.message}</p>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                    <span
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.85em',
                        fontWeight: 'bold',
                        backgroundColor: getStatusColor(app.status),
                        color: 'white'
                      }}
                    >
                      {app.status || 'pending'}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {app.status !== 'reviewed' && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleStatusUpdate(app.id!, 'reviewed')}
                      style={{ fontSize: '0.85em' }}
                    >
                      Mark as Reviewed
                    </button>
                  )}
                  {app.status !== 'accepted' && (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleStatusUpdate(app.id!, 'accepted')}
                      style={{ fontSize: '0.85em' }}
                    >
                      Accept
                    </button>
                  )}
                  {app.status !== 'rejected' && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleStatusUpdate(app.id!, 'rejected')}
                      style={{ fontSize: '0.85em', backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                    >
                      Reject
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VolunteerApplicationsManager;

